import { Copy } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import type { OrderActionItem } from './order-actions'
import type { SalesOrder } from '@/lib/api/sales-order-api'
import { useAuth } from '@/hooks/use-auth'
import { useDuplicateOrder } from '@/hooks/use-sales-order-query'
import { captureOrderDuplicate } from '@/lib/order-duplicate-tracking'
import { m } from '@/lib/paraglide/messages'

/** Lý do không sao chép được, cùng thứ tự kiểm với backend (Duplicate). */
function duplicateBlockedReason(order: SalesOrder): string | undefined {
    if (order.is_quick_sale) return m.order_duplicate_disabled_quick_sale()
    if (order.gift) return m.order_duplicate_disabled_gift()
    if (order.source_channel === 'kiotviet')
        return m.order_duplicate_disabled_kiotviet()
    return undefined
}

/**
 * "Sao chép đơn" (M-187): khách mua lại một đơn tương tự. Bấm là tạo ngay một
 * đơn NHÁP mới sao chép đơn đang xem rồi mở đơn đó; đơn gốc giữ nguyên.
 *
 * Không có hộp thoại xác nhận: thao tác không đụng đơn gốc và nháp chưa trừ
 * kho / chưa công nợ, bấm nhầm thì huỷ nháp. Hiện ở MỌI trạng thái cho người
 * có quyền tạo đơn; loại đơn không chép được thì vẫn hiện mờ, bấm vào báo lý
 * do (backend cũng chặn bằng mã ORDER_DUPLICATE_*).
 */
export function useDuplicateOrderAction({
    orderId,
    order,
}: {
    orderId: string
    order: SalesOrder | null | undefined
}): { item: OrderActionItem | null; isPending: boolean } {
    const { hasPermission } = useAuth()
    const navigate = useNavigate()
    const duplicateOrder = useDuplicateOrder()

    if (!order || !hasPermission('sales_orders.create')) {
        return { item: null, isPending: duplicateOrder.isPending }
    }

    const blockedReason = duplicateBlockedReason(order)
    const tracked = {
        sourceOrderId: orderId,
        sourceStatus: order.status,
        lineCount: order.items?.length ?? 0,
    }

    const submit = async () => {
        let newOrderId: string | undefined
        try {
            const created = await duplicateOrder.mutateAsync(orderId)
            newOrderId = created.data?.id
        } catch (error) {
            // Toast lỗi do hook lo; ở đây chỉ ghi nhận kết quả.
            captureOrderDuplicate({ ...tracked, error })
            return
        }
        if (!newOrderId) return
        captureOrderDuplicate({ ...tracked, newOrderId })
        void navigate({
            to: '/orders/$orderId',
            params: { orderId: newOrderId },
        })
    }

    if (blockedReason) {
        // Mờ nhưng bấm được để báo lý do: nút disabled không nhận hover/chạm,
        // lý do sẽ không hiện ở đâu cả (nhất là trong menu trên điện thoại).
        return {
            item: {
                key: 'duplicate',
                label: m.order_duplicate_btn(),
                icon: <Copy className="size-4" />,
                variant: 'outline',
                muted: true,
                disabled: false,
                title: blockedReason,
                onSelect: () => toast.info(blockedReason),
            },
            isPending: duplicateOrder.isPending,
        }
    }

    return {
        item: {
            key: 'duplicate',
            label: m.order_duplicate_btn(),
            icon: <Copy className="size-4" />,
            variant: 'outline',
            // Caller gộp thêm trạng thái bận của các hành động khác.
            disabled: duplicateOrder.isPending,
            onSelect: () => void submit(),
        },
        isPending: duplicateOrder.isPending,
    }
}
